'use-client';

import PauseResumeButton from '../../../PauseResumeButton/PauseResumeButton';
import { useSlideshowInteractions } from '../../../../common/slideShow/useSlideshowInteractions';
import React, { useEffect, useImperativeHandle } from 'react';
import ResponsiveContainer from '@wix/thunderbolt-elements/components/ResponsiveContainer';
import {
  getAccessibilityAttributes,
  getDataAttributes,
} from '@wix/editor-elements-common-utils';
import cn from 'clsx';
import { SlideshowContextProvider } from '../../SlideshowContext';
import type { ISlideshowImperativeActions, ISlideshowProps } from '../../types';
import style from './styles/Slideshow.scss';
import { TestIDs } from '../../constants';

const Slideshow: React.ForwardRefRenderFunction<
  ISlideshowImperativeActions,
  ISlideshowProps
> = (props, ref) => {
  const {
    id,
    children,
    className,
    containerProps,
    containerRootClassName,
    currentIndex,
    setCurrentIndex,
    isPlaying,
    initialIsPlaying,
    onPlay,
    onPause,
    onChange,
    onMouseEnter: onMouseEnterVelo,
    onMouseLeave: onMouseLeaveVelo,
    loop,
    navigatingForward,
    slideDuration,
    pauseOnHover,
    showPlaybackControl,
    direction,
    translations,
  } = props;

  const { ariaAttributes, role, tabIndex } = getAccessibilityAttributes(props);

  const {
    isFocused,
    setIsHovered,
    isHovered,
    isPaused,
    setIsPaused,
    setIsFocused,
    playbackControlButtonRef,
    onFocus,
  } = useSlideshowInteractions({
    autoplay: isPlaying,
  });
  const containerRef = React.useRef<HTMLDivElement>(null);

  useImperativeHandle(ref, () => ({
    setCurrentIndex,
  }));

  useEffect(() => {
    isPlaying ? onPlay?.({ type: 'onPlay' }) : onPause?.({ type: 'onPause' });
  }, [isPlaying, onPlay, onPause]);

  useEffect(() => {
    onChange?.({ type: 'onChange' });
  }, [currentIndex, onChange]);

  const onMouseEnter = (e: React.MouseEvent) => {
    setIsHovered(true);
    onMouseEnterVelo?.(e);
  };

  const onMouseLeave = (e: React.MouseEvent) => {
    setIsHovered(false);
    onMouseLeaveVelo?.(e);
  };

  return (
    <div
      id={id}
      ref={containerRef}
      data-testid={TestIDs.slideshow}
      {...getDataAttributes(props)}
      className={cn(containerRootClassName, className, style.slideshow)}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onFocus={onFocus}
      onBlur={() => {
        setIsFocused(false);
      }}
      {...ariaAttributes}
      role={role || 'group'}
      tabIndex={tabIndex}
    >
      <SlideshowContextProvider
        currentIndex={currentIndex}
        setCurrentIndex={setCurrentIndex}
        loop={loop}
        navigatingForward={navigatingForward}
        isPlaying={isPlaying}
        isHovered={isHovered}
        isFocused={isFocused}
        isPaused={isPaused}
        slideDuration={slideDuration}
        containerRef={containerRef}
        pauseOnHover={pauseOnHover}
        direction={direction}
      >
        <ResponsiveContainer {...containerProps}>
          {children}
        </ResponsiveContainer>
        {showPlaybackControl && initialIsPlaying && (
          <PauseResumeButton
            testId={TestIDs.pauseResumeButton}
            state={isPaused ? 'paused' : 'playing'}
            className={style.playbackControl}
            buttonRef={playbackControlButtonRef}
            pauseAriaLabel={translations.pauseControlAriaLabel}
            resumeAriaLabel={translations.playControlAriaLabel}
            onPause={() => setIsPaused(true)}
            onResume={() => setIsPaused(false)}
          />
        )}
      </SlideshowContextProvider>
    </div>
  );
};

export default React.forwardRef(Slideshow);
